/**
 * Default sample data for SnapReports / SnapDocs.
 *
 * When live connectors (GA4, GSC, DataForSEO, GBP) are not configured,
 * these values are injected so templates render with realistic placeholder
 * content.  Every key matches the `{{variable}}` tokens used inside
 * report / proposal / invoice page templates.
 *
 * Usage:
 *   import { REPORT_SAMPLE_VARS, mergeWithSampleDefaults } from './sample-data';
 *   const vars = mergeWithSampleDefaults(connectorVars);
 */

// ── GA4 (Google Analytics 4) ──────────────────────────────────────

export const GA4_SAMPLE: Record<string, string> = {
  ga4_sessions: '12,438',
  ga4_active_users: '8,214',
  ga4_conversions: '342',
  ga4_pageviews: '38,921',
  ga4_bounce_rate: '42.3%',
  ga4_avg_session_duration: '187s',
  ga4_date_range: '2025-06-01 – 2025-06-30',
};

// ── GSC (Google Search Console) ──────────────────────────────────

export const GSC_SAMPLE: Record<string, string> = {
  gsc_clicks: '6,812',
  gsc_impressions: '184,320',
  gsc_ctr: '3.70%',
  gsc_position: '14.2',
  gsc_date_range: '2025-06-01 – 2025-06-30',
  gsc_top_query_1: 'best marketing agency near me',
  gsc_top_query_1_clicks: '412',
  gsc_top_query_2: 'seo services small business',
  gsc_top_query_2_clicks: '287',
  gsc_top_query_3: 'local seo audit',
  gsc_top_query_3_clicks: '198',
  gsc_top_query_4: 'google business profile optimization',
  gsc_top_query_4_clicks: '156',
  gsc_top_query_5: 'website redesign cost',
  gsc_top_query_5_clicks: '104',
};

// ── DataForSEO ───────────────────────────────────────────────────

export const SEO_SAMPLE: Record<string, string> = {
  seo_domain_rank: '24,318',
  seo_organic_traffic: '9,450',
  seo_organic_keywords: '1,823',
  seo_backlinks: '3,412',
  seo_referring_domains: '287',
};

// ── Google Business Profile (GBP) ────────────────────────────────

export const GBP_SAMPLE: Record<string, string> = {
  gbp_rating: '4.7',
  gbp_total_reviews: '142',
  gbp_review_count_5: '98',
  gbp_review_count_4: '27',
  gbp_review_count_3: '11',
  gbp_review_count_2: '4',
  gbp_review_count_1: '2',
  gbp_recent_review_author: 'Sarah M.',
  gbp_recent_review_text: 'Absolutely phenomenal service. The team went above and beyond to deliver results. Highly recommend!',
  gbp_recent_review_rating: '5',
  gbp_business_name: 'Acme Digital Marketing',
  gbp_business_address: '123 Main Street, Suite 200, Austin, TX 78701',
  gbp_business_phone: '(512) 555-0190',
  gbp_business_website: 'https://acme-digital.example.com',
};

// ── General Report Variables ─────────────────────────────────────

export const GENERAL_SAMPLE: Record<string, string> = {
  report_title: 'Monthly Performance Report',
  report_subtitle: 'June 2025 Overview',
  report_date: 'July 1, 2025',
  report_period: 'June 1 – June 30, 2025',
  client_name: 'Acme Corp',
  client_website: 'acme-digital.example.com',
  agency_name: 'SnapLayer Studio',
  agency_logo: '',
  prepared_by: 'Marketing Team',
  prepared_for: 'John Smith, VP Marketing',
};

/**
 * All sample connector variables merged into one object.
 * Keys can be used as `{{key}}` in templates.
 */
export const REPORT_SAMPLE_VARS: Record<string, string> = {
  ...GENERAL_SAMPLE,
  ...GA4_SAMPLE,
  ...GSC_SAMPLE,
  ...SEO_SAMPLE,
  ...GBP_SAMPLE,
};

/**
 * Merge actual connector vars with sample defaults.
 * Actual (live) values always win; samples fill any gaps.
 */
export function mergeWithSampleDefaults(
  actual: Record<string, string>,
): Record<string, string> {
  return { ...REPORT_SAMPLE_VARS, ...actual };
}
